<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="#01579B" dark flat>
        <v-toolbar-title>Import Rapide des Menus</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-stepper v-model="currentStep" alt-labels>
        <v-stepper-header>
          <v-stepper-step :complete="currentStep > 1" step="1">
            Sélection des dates
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="currentStep > 2" step="2">
            Configuration des menus
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="currentStep > 3" step="3">
            Vérification et import
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <!-- Étape 1: Sélection des dates -->
          <v-stepper-content step="1">
            <v-card flat>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <h3 class="mb-3">Sélectionnez la période</h3>
                    <v-date-picker
                      v-model="selectedDates"
                      :allowed-dates="allowedDates"
                      :events="eventDates"
                      :event-color="getEventColor"
                      multiple
                      full-width
                      locale="fr-FR"
                      :min="minDate"
                      :max="maxDate"
                    ></v-date-picker>
                  </v-col>
                  <v-col cols="12" md="6">
                    <h3 class="mb-3">Actions rapides</h3>
                    
                    <!-- Sélection rapide -->
                    <v-card outlined class="mb-3">
                      <v-card-text>
                        <v-btn-toggle v-model="quickSelect" mandatory class="mb-3 d-flex justify-center" dense>
                          <v-btn value="week" small>
                            <span class="d-none d-sm-inline">Cette semaine</span>
                            <span class="d-sm-none">Semaine</span>
                          </v-btn>
                          <v-btn value="month" small>
                            <span class="d-none d-sm-inline">Ce mois</span>
                            <span class="d-sm-none">Mois</span>
                          </v-btn>
                          <v-btn value="custom" small>
                            <span class="d-none d-sm-inline">Personnalisé</span>
                            <span class="d-sm-none">Custom</span>
                          </v-btn>
                        </v-btn-toggle>
                        
                        <v-checkbox
                          v-model="excludeWeekends"
                          label="Exclure les week-ends"
                          hide-details
                          class="mt-2"
                        ></v-checkbox>
                        <v-checkbox
                          v-model="excludeDinner"
                          label="Exclure les dîners"
                          hide-details
                          class="mt-2"
                        ></v-checkbox>
                      </v-card-text>
                    </v-card>

                    <!-- Résumé -->
                    <v-alert type="info" outlined>
                      <strong>{{ selectedDates.length }} jour(s) sélectionné(s)</strong>
                      <v-chip-group column class="mt-2" v-if="selectedDates.length > 0">
                        <v-chip
                          v-for="date in selectedDates.slice(0, 5)"
                          :key="date"
                          small
                          close
                          @click:close="removeDate(date)"
                          :color="hasMenusOnDate(date) ? 'orange' : 'primary'"
                        >
                          {{ formatDate(date) }}
                          <v-icon v-if="hasMenusOnDate(date)" right small>mdi-alert</v-icon>
                        </v-chip>
                        <v-chip v-if="selectedDates.length > 5" small>
                          +{{ selectedDates.length - 5 }} autres
                        </v-chip>
                      </v-chip-group>
                    </v-alert>
                    
                    <!-- Menus existants pour les dates sélectionnées -->
                    <v-card v-if="selectedDates.length > 0 && hasAnyExistingMenus()" outlined class="mt-3">
                      <v-card-title class="py-2">
                        <v-icon left color="warning">mdi-alert</v-icon>
                        Menus existants sur les dates sélectionnées
                      </v-card-title>
                      <v-card-text>
                        <v-expansion-panels>
                          <v-expansion-panel 
                            v-for="date in selectedDates.filter(d => hasMenusOnDate(d))"
                            :key="date"
                          >
                            <v-expansion-panel-header>
                              <strong>{{ formatDateLong(date) }}</strong>
                              <v-spacer></v-spacer>
                              <span class="text--secondary">
                                {{ getExistingMenusForDate(date).length }} menu(s) existant(s)
                              </span>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <v-list dense>
                                <v-list-item 
                                  v-for="menu in getExistingMenusForDate(date)"
                                  :key="menu.id"
                                  class="px-0"
                                >
                                  <v-list-item-icon>
                                    <v-icon :color="menu.timeMeal === 'Déjeuner' ? 'orange' : 'blue'">
                                      {{ menu.timeMeal === 'Déjeuner' ? 'mdi-white-balance-sunny' : 'mdi-weather-night' }}
                                    </v-icon>
                                  </v-list-item-icon>
                                  <v-list-item-content>
                                    <v-list-item-title>{{ menu.name }}</v-list-item-title>
                                    <v-list-item-subtitle>
                                      {{ menu.timeMeal }} - {{ menu.restaurateur }}
                                      <span v-if="menu.description"> - {{ menu.description }}</span>
                                    </v-list-item-subtitle>
                                  </v-list-item-content>
                                </v-list-item>
                              </v-list>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="currentStep = 2" :disabled="selectedDates.length === 0">
                  Suivant
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-stepper-content>

          <!-- Étape 2: Configuration des menus -->
          <v-stepper-content step="2">
            <v-card flat>
              <v-card-text>
                <v-row>
                  <!-- Sélection du restaurateur -->
                  <v-col cols="12">
                    <v-select
                      v-model="selectedRestaurant"
                      :items="restaurants"
                      item-text="name"
                      item-value="code"
                      label="Restaurateur"
                      outlined
                      dense
                      prepend-inner-icon="mdi-store"
                      @change="onRestaurantChange"
                    >
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                          <v-list-item-subtitle>Code: {{ item.code }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-select>
                    
                    <!-- Debug info -->
                    <v-alert v-if="selectedRestaurant" type="info" dense text class="mt-2">
                      <small>
                        Restaurant sélectionné: {{ selectedRestaurant }} | 
                        Menus déjeuner: {{ getFilteredMenus('lunch').length }} | 
                        Menus dîner: {{ getFilteredMenus('dinner').length }}
                      </small>
                    </v-alert>
                  </v-col>

                  <!-- Templates de menus -->
                  <v-col cols="12" v-if="selectedRestaurant">
                    <v-tabs v-model="mealTypeTab" background-color="primary" dark>
                      <v-tab>
                        <v-icon left>mdi-white-balance-sunny</v-icon>
                        Déjeuner
                      </v-tab>
                      <v-tab v-if="!excludeDinner">
                        <v-icon left>mdi-weather-night</v-icon>
                        Dîner
                      </v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="mealTypeTab">
                      <!-- Déjeuner -->
                      <v-tab-item>
                        <v-card flat class="mt-3">
                          <v-card-title>
                            Menus du déjeuner
                          </v-card-title>
                          <v-card-text>
                            <!-- Liste des menus fréquents -->
                            <v-row>
                              <v-col cols="12" md="6">
                                <h4 class="mb-2">Sélectionner les menus</h4>
                                <!-- Autocomplete avec produits -->
                                <v-autocomplete
                                  v-model="lunchMenuInput"
                                  :items="getLunchMenuSuggestions()"
                                  item-text="name"
                                  item-value="name"
                                  label="Tapez un menu et appuyez sur Entrée"
                                  outlined
                                  dense
                                  clearable
                                  prepend-inner-icon="mdi-food"
                                  @keyup.enter="addLunchMenu"
                                  hide-details
                                  placeholder="Ex: Yassa poulet, Burger..."
                                  hide-no-data
                                  :search-input.sync="lunchSearchText"
                                  allow-overflow
                                  :append-icon="null"
                                ></v-autocomplete>
                                
                                <!-- Menus sélectionnés -->
                                <div v-if="selectedMenus.lunch.length > 0" class="mt-2">
                                  <v-chip
                                    v-for="(menu, index) in selectedMenus.lunch"
                                    :key="index"
                                    close
                                    @click:close="removeLunchMenu(menu)"
                                    color="primary"
                                    text-color="white"
                                    small
                                    class="mr-1 mb-1"
                                  >
                                    {{ typeof menu === 'string' ? menu : menu.name }}
                                  </v-chip>
                                </div>
                              </v-col>

                              <v-col cols="12" md="6">
                                <h4 class="mb-2">Menus fréquents</h4>
                                <v-chip-group column>
                                  <v-chip
                                    v-for="menu in getFrequentMenusForRestaurant('lunch')"
                                    :key="menu.name"
                                    @click="addFrequentMenu(menu, 'lunch')"
                                    outlined
                                    small
                                  >
                                    {{ menu.name }}
                                  </v-chip>
                                </v-chip-group>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-tab-item>

                      <!-- Dîner -->
                      <v-tab-item v-if="!excludeDinner">
                        <v-card flat class="mt-3">
                          <v-card-title>
                            Menus du dîner
                          </v-card-title>
                          <v-card-text>
                            <v-row>
                              <v-col cols="12" md="6">
                                <h4 class="mb-2">Sélectionner les menus</h4>
                                <!-- Autocomplete avec produits -->
                                <v-autocomplete
                                  v-model="dinnerMenuInput"
                                  :items="getDinnerMenuSuggestions()"
                                  item-text="name"
                                  item-value="name"
                                  label="Tapez un menu et appuyez sur Entrée"
                                  outlined
                                  dense
                                  clearable
                                  prepend-inner-icon="mdi-food"
                                  @keyup.enter="addDinnerMenu"
                                  hide-details
                                  placeholder="Ex: Yassa poulet *, Burger *..."
                                  hide-no-data
                                  :search-input.sync="dinnerSearchText"
                                  allow-overflow
                                  :append-icon="null"
                                ></v-autocomplete>
                                
                                <!-- Menus sélectionnés -->
                                <div v-if="selectedMenus.dinner.length > 0" class="mt-2">
                                  <v-chip
                                    v-for="(menu, index) in selectedMenus.dinner"
                                    :key="index"
                                    close
                                    @click:close="removeDinnerMenu(menu)"
                                    color="primary"
                                    text-color="white"
                                    small
                                    class="mr-1 mb-1"
                                  >
                                    {{ typeof menu === 'string' ? menu : menu.name }}
                                  </v-chip>
                                </div>
                              </v-col>

                              <v-col cols="12" md="6">
                                <h4 class="mb-2">Menus fréquents</h4>
                                <v-chip-group column>
                                  <v-chip
                                    v-for="menu in getFrequentMenusForRestaurant('dinner')"
                                    :key="menu.name"
                                    @click="addFrequentMenu(menu, 'dinner')"
                                    outlined
                                    small
                                  >
                                    {{ menu.name }}
                                  </v-chip>
                                </v-chip-group>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-btn text @click="currentStep = 1">
                  <v-icon left>mdi-arrow-left</v-icon>
                  Retour
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn 
                  color="primary" 
                  @click="currentStep = 3"
                  :disabled="!canProceedToStep3"
                >
                  Suivant
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-stepper-content>

          <!-- Étape 3: Vérification et import -->
          <v-stepper-content step="3">
            <v-card flat>
              <v-card-title>Résumé de l'import</v-card-title>
              <v-card-text>
                <v-alert type="info" outlined class="mb-3">
                  <strong>{{ totalMenusToCreate }} menus</strong> seront créés pour 
                  <strong>{{ selectedDates.length }} jours</strong>
                </v-alert>

                <!-- Aperçu des menus par date -->
                <v-expansion-panels>
                  <v-expansion-panel v-for="date in selectedDates.slice(0, 5)" :key="date">
                    <v-expansion-panel-header>
                      <strong>{{ formatDateLong(date) }}</strong>
                      <v-spacer></v-spacer>
                      <span class="text--secondary">
                        {{ getMenuCountForDate(date) }} menu(s)
                      </span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-list dense>
                        <v-list-item-group>
                          <v-list-item v-if="selectedMenus.lunch.length > 0">
                            <v-list-item-icon>
                              <v-icon color="orange">mdi-white-balance-sunny</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>Déjeuner</v-list-item-title>
                              <v-list-item-subtitle>
                                <v-chip 
                                  v-for="menu in selectedMenus.lunch" 
                                  :key="menu.name"
                                  x-small
                                  class="mr-1"
                                >
                                  {{ menu.name }}
                                </v-chip>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item v-if="selectedMenus.dinner.length > 0">
                            <v-list-item-icon>
                              <v-icon color="blue">mdi-weather-night</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>Dîner</v-list-item-title>
                              <v-list-item-subtitle>
                                <v-chip 
                                  v-for="menu in selectedMenus.dinner" 
                                  :key="menu.name"
                                  x-small
                                  class="mr-1"
                                >
                                  {{ menu.name }}
                                </v-chip>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>

                <v-alert v-if="selectedDates.length > 5" type="info" text class="mt-2">
                  ... et {{ selectedDates.length - 5 }} autres dates
                </v-alert>
              </v-card-text>
              <v-card-actions>
                <v-btn text @click="currentStep = 2">
                  <v-icon left>mdi-arrow-left</v-icon>
                  Retour
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn 
                  color="success" 
                  @click="importMenus"
                  :loading="importing"
                >
                  <v-icon left>mdi-cloud-upload</v-icon>
                  Importer les menus
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

    </v-card>
  </v-container>
</template>

<script>
import moment from 'moment'
import apiClient from '../../../config/api'

export default {
  name: 'MenuBulkImport',
  props: {
    products: {
      type: Array,
      default: () => []
    },
    restaurateurs: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentStep: 1,
      selectedDates: [],
      selectedRestaurant: null,
      mealTypeTab: 0,
      quickSelect: 'week',
      excludeWeekends: true,
      excludeDinner: false,
      importing: false,
      
      // Inputs pour ajouter des menus avec autocomplétion
      lunchMenuInput: '',
      dinnerMenuInput: '',
      lunchSearchText: '',
      dinnerSearchText: '',
      
      // Menus sélectionnés par type
      selectedMenus: {
        lunch: [],
        dinner: []
      },
      
      // Restaurants disponibles (sera mis à jour depuis les props)
      restaurants: [],
      
      // Menus fréquents par restaurateur
      frequentMenusByRestaurant: {
        'ND': {
          lunch: ['Tacos', 'Burger', 'Pizza', 'Chawarma', 'Norvégien'],
          dinner: ['Tacos *', 'Burger *', 'Pizza *', 'Chawarma *', 'Norvégien *']
        },
        'MY': {
          lunch: ['Burger', 'Chawarma poulet', 'Norvégien', 'Chawarma viande'],
          dinner: ['Burger *', 'Chawarma poulet *', 'Norvégien *', 'Chawarma viande *']
        },
        'default': {
          lunch: [],
          dinner: []
        }
      },
      
      // Menus organisés depuis les produits
      frequentMenus: {
        lunch: [],
        dinner: []
      },
      
      // Menus existants par date (pour éviter les doublons)
      existingMenusByDate: {}
    }
  },
  
  computed: {
    minDate() {
      return moment().format('YYYY-MM-DD')
    },
    
    maxDate() {
      return moment().add(3, 'months').format('YYYY-MM-DD')
    },
    
    canProceedToStep3() {
      return this.selectedRestaurant && 
             (this.selectedMenus.lunch.length > 0 || this.selectedMenus.dinner.length > 0)
    },
    
    totalMenusToCreate() {
      const lunchCount = this.selectedMenus.lunch.length * this.selectedDates.length
      const dinnerCount = this.selectedMenus.dinner.length * this.selectedDates.length
      return lunchCount + dinnerCount
    },
    
    eventDates() {
      // Dates avec des menus existants
      return Object.keys(this.existingMenusByDate)
    },
    
    isRestaurantSelected() {
      const result = this.selectedRestaurant && this.selectedRestaurant !== '' && this.selectedRestaurant !== null
      console.log('isRestaurantSelected computed:', result, 'selectedRestaurant:', this.selectedRestaurant)
      return result
    }
  },
  
  watch: {
    quickSelect(val) {
      this.updateDateSelection(val)
    },
    
    excludeWeekends() {
      if (this.excludeWeekends) {
        this.selectedDates = this.selectedDates.filter(date => {
          const dayOfWeek = moment(date).day()
          return dayOfWeek !== 0 && dayOfWeek !== 6
        })
      }
    },
    
    selectedRestaurant() {
      // Réorganiser les produits quand le restaurateur change
      this.organizeProducts()
      // Réinitialiser les sélections
      this.selectedMenus.lunch = []
      this.selectedMenus.dinner = []
    }
  },
  
  mounted() {
    this.initializeComponent()
    this.updateDateSelection('week')
  },
  
  methods: {
    updateDateSelection(type) {
      const dates = []
      const start = moment()
      let end
      
      switch(type) {
        case 'week':
          end = moment().endOf('week')
          break
        case 'month':
          end = moment().endOf('month')
          break
        default:
          return
      }
      
      for (let m = start; m.isSameOrBefore(end); m.add(1, 'days')) {
        if (!this.excludeWeekends || (m.day() !== 0 && m.day() !== 6)) {
          dates.push(m.format('YYYY-MM-DD'))
        }
      }
      
      this.selectedDates = dates
    },
    
    allowedDates(val) {
      // Autoriser uniquement les jours ouvrables si l'option est activée
      if (this.excludeWeekends) {
        const dayOfWeek = moment(val).day()
        return dayOfWeek !== 0 && dayOfWeek !== 6
      }
      return true
    },
    
    getEventColor(date) {
      // Couleur pour les dates avec des menus existants
      return 'green'
    },
    
    formatDate(date) {
      return moment(date).format('DD/MM')
    },
    
    formatDateLong(date) {
      moment.locale('fr')
      return moment(date).format('dddd DD MMMM YYYY')
    },
    
    removeDate(date) {
      const index = this.selectedDates.indexOf(date)
      if (index > -1) {
        this.selectedDates.splice(index, 1)
      }
    },
    
    toggleMenu(menu, mealType) {
      const key = mealType === 'Déjeuner' ? 'lunch' : 'dinner'
      const index = this.selectedMenus[key].findIndex(m => m.id === menu.id)
      
      if (index > -1) {
        this.selectedMenus[key].splice(index, 1)
      } else {
        this.selectedMenus[key].push(menu)
      }
    },
    
    isMenuSelected(menu, mealType) {
      const key = mealType === 'Déjeuner' ? 'lunch' : 'dinner'
      return this.selectedMenus[key].some(m => m.id === menu.id)
    },
    
    removeMenu(index, mealType) {
      const key = mealType === 'Déjeuner' ? 'lunch' : 'dinner'
      this.selectedMenus[key].splice(index, 1)
    },
    
    
    getMenuCountForDate(date) {
      return this.selectedMenus.lunch.length + this.selectedMenus.dinner.length
    },
    
    initializeComponent() {
      // Initialiser les restaurants depuis les props ou utiliser recupRestaurateur
      this.recupRestaurateur()
      
      // Organiser les produits existants comme menus fréquents
      this.organizeProducts()
      
      // Charger les menus existants
      this.loadExistingMenus()
    },
    
    recupRestaurateur() {
      // Extraire les restaurateurs uniques depuis les produits existants
      if (this.products && this.products.length > 0) {
        const uniqueRestaurants = new Set()
        
        this.products.forEach(product => {
          if (product.originProduct && product.originProduct.trim()) {
            uniqueRestaurants.add(product.originProduct.trim())
          }
        })
        
        // Convertir en format requis
        this.restaurants = Array.from(uniqueRestaurants).map(code => ({
          code: code,
          name: this.getRestaurantName(code)
        }))
        
        console.log('Restaurants trouvés dans les produits:', this.restaurants)
      }
      
      // Si pas de produits ou restaurateurs vides, utiliser sessionStorage
      if (this.restaurants.length === 0) {
        if (this.restaurateurs && this.restaurateurs.length > 0) {
          this.restaurants = this.restaurateurs.map(r => ({
            code: r,
            name: r
          }))
        } else {
          // Valeurs par défaut
          this.restaurants = [
            { code: 'MY', name: 'MAAY' },
            { code: 'ND', name: 'Ndellice' },
            { code: 'AN', name: 'ANACIM' }
          ]
        }
      }
    },
    
    getRestaurantName(code) {
      // Mapper les codes vers les noms complets
      const restaurantNames = {
        'MY': 'MAAY',
        'ND': 'Ndellice', 
        'AN': 'ANACIM',
        'ANACIM': 'ANACIM'
      }
      
      return restaurantNames[code] || code
    },
    
    organizeProducts() {
      if (!this.products || this.products.length === 0) return
      
      // Réinitialiser les menus
      this.frequentMenus = {
        lunch: [],
        dinner: []
      }
      
      // Filtrer par restaurateur si sélectionné
      const filteredProducts = this.selectedRestaurant 
        ? this.products.filter(p => p.originProduct === this.selectedRestaurant)
        : this.products
      
      // Grouper les produits uniques
      const uniqueMenus = new Map()
      
      filteredProducts.forEach(product => {
        if (!uniqueMenus.has(product.name)) {
          uniqueMenus.set(product.name, {
            id: product.id,
            name: product.name,
            description: product.description || '',
            price: product.price || 0,
            originProduct: product.originProduct
          })
        }
      })
      
      // Séparer en déjeuner et dîner basé sur le caractère *
      uniqueMenus.forEach((menu, name) => {
        // Les menus avec * sont pour le dîner
        if (name.includes('*')) {
          this.frequentMenus.dinner.push({
            ...menu,
            name: menu.name // Garder le * pour le dîner
          })
        } else {
          this.frequentMenus.lunch.push(menu)
        }
      })
    },
    
    getFilteredMenus(type) {
      // Retourner les menus filtrés par restaurateur et type
      const menus = type === 'lunch' ? this.frequentMenus.lunch : this.frequentMenus.dinner
      
      if (!this.selectedRestaurant) {
        return menus
      }
      
      return menus.filter(m => 
        !m.originProduct || m.originProduct === this.selectedRestaurant
      )
    },
    
    getFrequentMenusForRestaurant(type) {
      // Retourner les menus fréquents spécifiques au restaurateur
      if (!this.selectedRestaurant) return []
      
      const restaurantMenus = this.frequentMenusByRestaurant[this.selectedRestaurant] || 
                              this.frequentMenusByRestaurant.default
      
      const menuNames = type === 'lunch' ? restaurantMenus.lunch : restaurantMenus.dinner
      
      return menuNames.map(name => ({ name }))
    },
    
    addFrequentMenu(menu, type) {
      // Ajouter un menu fréquent à la sélection
      const key = type
      if (!this.selectedMenus[key].find(m => 
        (typeof m === 'string' ? m : m.name) === menu.name
      )) {
        this.selectedMenus[key].push(menu)
      }
    },
    
    removeMenuChip(index, type) {
      this.selectedMenus[type].splice(index, 1)
    },
    
    onRestaurantChange() {
      console.log('Restaurant sélectionné:', this.selectedRestaurant)
      console.log('Restaurants disponibles:', this.restaurants)
      console.log('Menus disponibles après sélection:', {
        lunch: this.getFilteredMenus('lunch'),
        dinner: this.getFilteredMenus('dinner')
      })
    },
    
    addLunchMenu() {
      // Utiliser le texte de recherche si disponible, sinon la valeur sélectionnée
      const inputText = this.lunchSearchText || this.lunchMenuInput
      if (inputText && inputText.trim()) {
        const menuName = inputText.trim()
        if (!this.selectedMenus.lunch.some(m => 
          (typeof m === 'string' ? m : m.name) === menuName
        )) {
          this.selectedMenus.lunch.push(menuName)
        }
        this.lunchMenuInput = ''
        this.lunchSearchText = ''
      }
    },
    
    addDinnerMenu() {
      // Utiliser le texte de recherche si disponible, sinon la valeur sélectionnée
      const inputText = this.dinnerSearchText || this.dinnerMenuInput
      if (inputText && inputText.trim()) {
        let menuName = inputText.trim()
        // Ajouter automatiquement * si pas présent pour les menus du dîner
        if (!menuName.includes('*')) {
          menuName += ' *'
        }
        
        if (!this.selectedMenus.dinner.some(m => 
          (typeof m === 'string' ? m : m.name) === menuName
        )) {
          this.selectedMenus.dinner.push(menuName)
        }
        this.dinnerMenuInput = ''
        this.dinnerSearchText = ''
      }
    },
    
    removeLunchMenu(item) {
      const index = this.selectedMenus.lunch.findIndex(m => 
        (typeof m === 'string' ? m : m.name) === (typeof item === 'string' ? item : item.name)
      )
      if (index > -1) {
        this.selectedMenus.lunch.splice(index, 1)
      }
    },
    
    removeDinnerMenu(item) {
      const index = this.selectedMenus.dinner.findIndex(m => 
        (typeof m === 'string' ? m : m.name) === (typeof item === 'string' ? item : item.name)
      )
      if (index > -1) {
        this.selectedMenus.dinner.splice(index, 1)
      }
    },
    
    getLunchMenuSuggestions() {
      // Combiner les menus des produits et les menus fréquents
      const suggestions = new Map()
      
      console.log('Getting lunch suggestions:', {
        selectedRestaurant: this.selectedRestaurant,
        products: this.products?.length || 0,
        frequentMenus: this.frequentMenus
      })
      
      // Ajouter les menus fréquents du restaurateur
      const frequentMenus = this.getFrequentMenusForRestaurant('lunch')
      frequentMenus.forEach(menu => {
        suggestions.set(menu.name, { name: menu.name })
      })
      
      // Ajouter les produits filtrés (déjeuner uniquement - sans *)
      const filteredMenus = this.getFilteredMenus('lunch')
      filteredMenus.forEach(menu => {
        const name = typeof menu === 'string' ? menu : menu.name
        if (!name.includes('*')) {
          suggestions.set(name, { name })
        }
      })
      
      const result = Array.from(suggestions.values()).sort((a, b) => a.name.localeCompare(b.name))
      console.log('Lunch suggestions result:', result)
      return result
    },
    
    getDinnerMenuSuggestions() {
      // Combiner les menus des produits et les menus fréquents
      const suggestions = new Map()
      
      console.log('Getting dinner suggestions:', {
        selectedRestaurant: this.selectedRestaurant,
        products: this.products?.length || 0,
        frequentMenus: this.frequentMenus
      })
      
      // Ajouter les menus fréquents du restaurateur
      const frequentMenus = this.getFrequentMenusForRestaurant('dinner')
      frequentMenus.forEach(menu => {
        suggestions.set(menu.name, { name: menu.name })
      })
      
      // Ajouter les produits filtrés (dîner uniquement - avec *)
      const filteredMenus = this.getFilteredMenus('dinner')
      filteredMenus.forEach(menu => {
        const name = typeof menu === 'string' ? menu : menu.name
        suggestions.set(name, { name })
      })
      
      // Ajouter les versions dîner des menus déjeuner
      const lunchMenus = this.getFilteredMenus('lunch')
      lunchMenus.forEach(menu => {
        const name = typeof menu === 'string' ? menu : menu.name
        if (!name.includes('*')) {
          const dinnerName = name + ' *'
          suggestions.set(dinnerName, { name: dinnerName })
        }
      })
      
      const result = Array.from(suggestions.values()).sort((a, b) => a.name.localeCompare(b.name))
      console.log('Dinner suggestions result:', result)
      return result
    },
    
    hasMenusOnDate(date) {
      return this.existingMenusByDate[date] && this.existingMenusByDate[date].length > 0
    },
    
    hasAnyExistingMenus() {
      return this.selectedDates.some(date => this.hasMenusOnDate(date))
    },
    
    getExistingMenusForDate(date) {
      return this.existingMenusByDate[date] || []
    },
    
    async loadExistingMenus() {
      // Utiliser les produits déjà chargés depuis le parent
      if (this.products && this.products.length > 0) {
        
        // Organiser les menus par date
        this.existingMenusByDate = {}
        let menusCount = 0
        
        this.products.forEach((product) => {
          if (product.listDates) {
            // listDates peut contenir plusieurs dates séparées par des virgules
            const dates = product.listDates.split(',')
              .map(d => d.trim())
              .filter(d => d && d.length > 0) // Supprimer les dates vides
            
            dates.forEach(dateStr => {
              // Les dates semblent être déjà au format YYYY-MM-DD
              let productDate = dateStr
              
              // Vérifier si c'est une date valide
              if (moment(productDate, 'YYYY-MM-DD', true).isValid()) {
                // Date déjà au bon format
                productDate = dateStr
              } else if (moment(productDate, 'DD-MM-YYYY', true).isValid()) {
                // Convertir du format DD-MM-YYYY vers YYYY-MM-DD
                productDate = moment(dateStr, 'DD-MM-YYYY').format('YYYY-MM-DD')
              } else {
                // Date invalide, ignorer
                console.warn('Date invalide ignorée:', dateStr)
                return
              }
              
              if (!this.existingMenusByDate[productDate]) {
                this.existingMenusByDate[productDate] = []
              }
              
              this.existingMenusByDate[productDate].push({
                id: product.id,
                name: product.name,
                description: product.description || '',
                timeMeal: product.timeMeal || '',
                restaurateur: product.originProduct || '',
                planningDate: productDate
              })
              menusCount++
            })
          }
        })
        
        console.log(`${menusCount} menus organisés dans ${Object.keys(this.existingMenusByDate).length} dates`)
        console.log('Dates avec menus:', Object.keys(this.existingMenusByDate))
        
        // Debug spécifique pour le 29/08/2025
        const today = '2025-08-29'
        if (this.existingMenusByDate[today]) {
          console.log(`Menus trouvés pour ${today}:`, this.existingMenusByDate[today].length)
          console.log('Exemple de menu du jour:', this.existingMenusByDate[today][0])
        } else {
          console.log(`Aucun menu trouvé pour ${today}`)
          // Chercher d'autres dates autour d'aujourd'hui
          const nearDates = Object.keys(this.existingMenusByDate)
            .filter(date => date.includes('2025-08'))
            .slice(0, 5)
          console.log('Dates disponibles en août 2025:', nearDates)
        }
        
      } else {
        console.log('Aucun produit disponible pour organiser')
      }
    },
    
    async importMenus() {
      this.importing = true
      
      try {
        // Préparer les données pour l'import
        const menusToImport = []
        
        for (const date of this.selectedDates) {
          // Ajouter les menus du déjeuner
          for (const menu of this.selectedMenus.lunch) {
            let menuName = typeof menu === 'string' ? menu : menu.name
            const menuData = {
              name: menuName,
              price: process.env.VUE_APP_APP_PRODUCT_PRICE || '2000',
              description: (typeof menu === 'object' ? menu.description : '') || '',
              listDates: moment(date).format('YYYY-MM-DD'),
              timeMeal: 'Déjeuner',
              originProduct: this.selectedRestaurant
            }
            menusToImport.push(menuData)
          }
          
          // Ajouter les menus du dîner seulement si pas exclus
          if (!this.excludeDinner) {
            for (const menu of this.selectedMenus.dinner) {
              let menuName = typeof menu === 'string' ? menu : menu.name
              const menuData = {
                name: menuName,
                price: process.env.VUE_APP_APP_PRODUCT_PRICE || '2000',
                description: (typeof menu === 'object' ? menu.description : '') || '',
                listDates: moment(date).format('YYYY-MM-DD'),
                timeMeal: 'Dîner',
                originProduct: this.selectedRestaurant
              }
              menusToImport.push(menuData)
            }
          }
        }
        
        
        // Envoyer à l'API (utilise le même endpoint que l'import Excel)
        const response = await apiClient.post(
          `${this.$store.getters.user.baseUrl}/product/createList/` + sessionStorage.getItem("userId"),
          menusToImport
        )
        
        if (response.data && response.data.status === 200) {
          this.$emit('success', {
            message: `${menusToImport.length} menus importés avec succès`,
            data: response.data.datas
          })
        }
      } catch (error) {
        this.$emit('error', {
          message: 'Erreur lors de l\'import des menus',
          error: error
        })
      } finally {
        this.importing = false
      }
    }
  }
}
</script>

<style scoped>
.v-stepper__content {
  padding: 24px;
}

.v-chip-group {
  max-height: 200px;
  overflow-y: auto;
}
</style>